import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import type { ProductMoney } from '#components/product-finance/money';

export type NextDuePayment = Readonly<{
  id: string;
  name: string;
  amount: ProductMoney;
  date: string;
  /** Whole days from the dashboard date; 0 means due today. */
  daysUntilDue: number;
}>;

export type PaymentOutlook = Readonly<{
  nextDue: NextDuePayment | null;
  overdueCount: number;
  overdueMinorUnits: number;
  oldestOverdueDate: string | null;
  paidCount: number;
}>;

type OutlookInput = Pick<
  ReadyFinancialDashboard,
  | 'asOfDate'
  | 'overdueCommitments'
  | 'pendingCurrentCycleCommitments'
  | 'futureCycleCommitments'
  | 'unassignedCommitments'
  | 'upcomingCommitments'
  | 'paidCurrentCycleCommitments'
>;

/**
 * Summarises what the dashboard already loaded so the user can see the next
 * due date and the overdue total without scanning every list. It creates no
 * movement: a payment stays an expectation until the user confirms it.
 */
export function summarizePaymentOutlook(
  dashboard: OutlookInput,
): PaymentOutlook {
  const overdueIds = new Set(
    dashboard.overdueCommitments.map(item => item.occurrence.id),
  );
  const candidates = new Map<string, NextDuePayment>();
  for (const item of [
    ...dashboard.pendingCurrentCycleCommitments,
    ...dashboard.futureCycleCommitments,
    ...dashboard.unassignedCommitments,
    ...dashboard.upcomingCommitments,
  ]) {
    const { id, name, amount, date } = item.occurrence;
    if (overdueIds.has(id) || candidates.has(id) || date < dashboard.asOfDate) {
      continue;
    }
    candidates.set(id, {
      id,
      name,
      amount,
      date,
      daysUntilDue: daysBetween(dashboard.asOfDate, date),
    });
  }
  const nextDue =
    [...candidates.values()].sort(
      (left, right) =>
        left.date.localeCompare(right.date) || left.id.localeCompare(right.id),
    )[0] ?? null;
  const overdueDates = dashboard.overdueCommitments
    .map(item => item.occurrence.date)
    .sort();

  return Object.freeze({
    nextDue,
    overdueCount: dashboard.overdueCommitments.length,
    overdueMinorUnits: dashboard.overdueCommitments.reduce(
      (sum, item) => sum + item.occurrence.amount.minorUnits,
      0,
    ),
    oldestOverdueDate: overdueDates[0] ?? null,
    paidCount: dashboard.paidCurrentCycleCommitments.filter(
      item => item.status === 'matched',
    ).length,
  });
}

function daysBetween(fromDate: string, toDate: string): number {
  const toUtc = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((toUtc(toDate) - toUtc(fromDate)) / 86_400_000);
}
